import { useProfile } from "~/store/profile";

type Coupon = {
  card_number: string;
  short_code: string;
  ts_cpn_assign: string;
  ts_prog_exp: string;
  unique_code: string;
};

type TierData = {
  label: string;
  points: string;
  return_period: string;
  free_shipping_min_total: string;
};

type Settings = {
  enabled: boolean;
  program_date_start: string;
  program_date_end: string;
  points_per_coupon: number;
  coupon_redemption_date_start: string;
  coupon_redemption_date_end: string;
  tiers: Record<string, TierData>;
};

type FidelityData = {
  tier: string;
  next_tier: string;
  points: number;
  coupons: Coupon[];
  settings: Settings;
};

export const useFidelityStore = defineStore("fidelity", () => {
  const profileStore = useProfile();
  const fidelityData = ref<FidelityData | null>(null);
  const isFetching = ref(false);

  const fetchFidelityData = async () => {
    if (fidelityData.value || isFetching.value) return;
    isFetching.value = true;

    const { data } = await useLazyFetch<FidelityData>("/api/fidelity", {
      key: "cachedRequest",
      method: "POST",
      body: {
        cardNumber: profileStore.profile.codiceCard ?? '111',
      },
    });

    fidelityData.value = data.value;
    isFetching.value = false;
  };

  const getFidelityData = async () => {
    if (!fidelityData.value) {
      await fetchFidelityData();
    }
    return fidelityData.value;
  };

  const points = computed(() => fidelityData.value?.points ?? 0);

  const pointDifference = computed(() =>
    fidelityData?.value?.tier &&
    fidelityData?.value?.next_tier !== null &&
    fidelityData.value?.settings
      ? parseInt(
          fidelityData.value.settings.tiers[fidelityData?.value?.next_tier]
            .points
        ) - fidelityData.value.points
      : 200
  );

  const isGold = computed(() => fidelityData.value?.tier === "gold");

  const pointsPerCoupon = computed(() =>
    fidelityData.value?.settings
      ? fidelityData.value?.settings?.points_per_coupon
      : 200
  );

  const nextTierPoints = computed(() =>
    fidelityData?.value?.next_tier &&
    fidelityData?.value?.next_tier !== null &&
    fidelityData.value?.settings
      ? parseInt(
          fidelityData.value.settings.tiers[fidelityData?.value?.next_tier]
            .points
        )
      : 200 + points.value
  );

  const nextTierStatusLabel = computed(() =>
    fidelityData?.value?.next_tier && fidelityData.value?.settings
      ? fidelityData.value.settings.tiers[fidelityData?.value?.next_tier].label
      : "lover gold"
  );

  const currentTierStatusLabel = computed(() =>
    fidelityData?.value?.tier && fidelityData.value?.settings
      ? fidelityData.value.settings.tiers[fidelityData?.value?.tier].label
      : "standart"
  );

  const tierDataReturnPeriod = computed(() =>
    fidelityData?.value?.tier && fidelityData.value?.settings
      ? fidelityData.value.settings.tiers[fidelityData?.value?.tier]
          .return_period
      : "30"
  );

  const nextTierDataReturnPeriod = computed(() =>
    fidelityData?.value?.next_tier && fidelityData.value?.settings
      ? fidelityData.value.settings.tiers[fidelityData?.value?.next_tier]
          .return_period
      : "40"
  );

  const couponRedemptionDateEnd = computed(() =>
    fidelityData?.value &&
    fidelityData.value?.settings &&
    fidelityData.value?.settings.coupon_redemption_date_end
      ? fidelityData.value?.settings.coupon_redemption_date_end
      : ""
  );

  const programDateEnd = computed(() =>
    fidelityData?.value &&
    fidelityData.value?.settings &&
    fidelityData.value?.settings.program_date_end
      ? fidelityData.value?.settings.program_date_end
      : ""
  );

  const pointsDiffFromPrevCoupon = () => {
    const ppc = pointsPerCoupon.value;
    if (ppc <= 0 || points.value < 0) return 0;
    return points.value % ppc;
  };

  const pointsDiffFromNextCoupon = () => {
    const ppc = pointsPerCoupon.value;
    if (ppc <= 0 || points.value < 0) return 0;
    return ppc - pointsDiffFromPrevCoupon();
  };

  return {
    fidelityData,
    isFetching,
    fetchFidelityData,
    getFidelityData,
    pointsDiffFromPrevCoupon,
    pointsDiffFromNextCoupon,
    points,
    pointDifference,
    isGold,
    nextTierPoints,
    pointsPerCoupon,
    nextTierStatusLabel,
    currentTierStatusLabel,
    tierDataReturnPeriod,
    nextTierDataReturnPeriod,
    couponRedemptionDateEnd,
    programDateEnd,
  };
});
